// src/components/FemaleJourneyRewardsPage.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaGift,
  FaUserFriends,
  FaPhoneAlt,
  FaCrown,
  FaChevronLeft,
} from "react-icons/fa";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { AuthContext } from "../context/AuthContext";
import TopBar from "../components/common/TopBar";
import BottomNav from "./BottomNav";
import tokenImg from "../assets/token.png";
import "./MyRankRewardsPage.css";

const INVITE_REWARDS = [
  {
    id: "female_invite_10",
    category: "invite",
    target: 10,
    rewardType: "tokens",
    rewardValue: 30,
    title: "Invite 10 female friends",
    icon: "invite",
  },
  {
    id: "female_invite_20",
    category: "invite",
    target: 20,
    rewardType: "tokens",
    rewardValue: 30,
    title: "Invite 20 female friends",
    icon: "invite",
  },
  {
    id: "female_invite_30",
    category: "invite",
    target: 30,
    rewardType: "tokens",
    rewardValue: 30,
    title: "Invite 30 female friends",
    icon: "gift",
  },
];

const LEVEL_REWARDS = [
  {
    id: "female_level_1",
    category: "level",
    target: 1,
    rewardType: "tokens",
    rewardValue: 10,
    title: "Reach level 1",
    icon: "level",
  },
  {
    id: "female_level_2",
    category: "level",
    target: 2,
    rewardType: "tokens",
    rewardValue: 20,
    title: "Reach level 2",
    icon: "level",
  },
  {
    id: "female_level_3",
    category: "level",
    target: 3,
    rewardType: "tokens",
    rewardValue: 30,
    title: "Reach level 3",
    icon: "level",
  },
  {
    id: "female_level_4",
    category: "level",
    target: 4,
    rewardType: "tokens",
    rewardValue: 40,
    title: "Reach level 4",
    icon: "level",
  },
  {
    id: "female_level_5",
    category: "level",
    target: 5,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Reach level 5",
    icon: "level",
  },
  {
    id: "female_level_6",
    category: "level",
    target: 6,
    rewardType: "tokens",
    rewardValue: 60,
    title: "Reach level 6",
    icon: "level",
  },
  {
    id: "female_level_7",
    category: "level",
    target: 7,
    rewardType: "tokens",
    rewardValue: 70,
    title: "Reach level 7",
    icon: "level",
  },
  {
    id: "female_level_8",
    category: "level",
    target: 8,
    rewardType: "tokens",
    rewardValue: 80,
    title: "Reach level 8",
    icon: "level",
  },
  {
    id: "female_level_9",
    category: "level",
    target: 9,
    rewardType: "tokens",
    rewardValue: 90,
    title: "Reach level 9",
    icon: "level",
  },
  {
    id: "female_level_10",
    category: "level",
    target: 10,
    rewardType: "tokens",
    rewardValue: 100,
    title: "Reach level 10",
    icon: "gift",
  },
];

const CALL_REWARDS = [
  {
    id: "female_call_60",
    category: "call",
    target: 60,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Complete 60 call minutes",
    icon: "call",
  },
  {
    id: "female_call_120",
    category: "call",
    target: 120,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Complete 120 call minutes",
    icon: "call",
  },
  {
    id: "female_call_180",
    category: "call",
    target: 180,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Complete 180 call minutes",
    icon: "call",
  },
  {
    id: "female_call_240",
    category: "call",
    target: 240,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Complete 240 call minutes",
    icon: "call",
  },
  {
    id: "female_call_300",
    category: "call",
    target: 300,
    rewardType: "tokens",
    rewardValue: 50,
    title: "Complete 300 call minutes",
    icon: "gift",
  },
];

function getNumeric(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export default function FemaleJourneyRewardsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isReady } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState("invite");
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState("");
  const [gender, setGender] = useState("");
  const [inviteCount, setInviteCount] = useState(0);
  const [femaleLevel, setFemaleLevel] = useState(0);
  const [totalCallMinutes, setTotalCallMinutes] = useState(0);
  const [claimedRewardIds, setClaimedRewardIds] = useState([]);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!isReady) return;

    if (!user) {
      navigate("/");
      return;
    }

    const userRef = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setLoading(false);
          navigate("/select-gender");
          return;
        }

        const data = snap.data() || {};
        const userGender = String(data.gender || "").toLowerCase();

        setGender(userGender);
        setInviteCount(getNumeric(data.inviteCount, 0));
        setFemaleLevel(
          getNumeric(
            data.femaleLevel ??
              data.level ??
              data.currentLevel,
            0
          )
        );
        setTotalCallMinutes(
          getNumeric(
            data.totalCallMinutes ??
              data.callMinutes ??
              data.totalMinutes,
            0
          )
        );
        setClaimedRewardIds(
          Array.isArray(data.claimedRewardIds) ? data.claimedRewardIds : []
        );

        setLoading(false);

        if (userGender === "male") {
          navigate("/my-profile");
        }
      },
      (error) => {
        console.error("FemaleJourneyRewardsPage snapshot error:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isReady, user, navigate]);

  const functionsEurope = useMemo(
    () => getFunctions(undefined, "europe-west1"),
    []
  );

  const claimFemaleReward = useMemo(
    () => httpsCallable(functionsEurope, "claimFemaleReward"),
    [functionsEurope]
  );

  const enrichReward = (reward) => {
    let progress = 0;

    if (reward.category === "invite") {
      progress = inviteCount;
    } else if (reward.category === "level") {
      progress = femaleLevel;
    } else if (reward.category === "call") {
      progress = totalCallMinutes;
    }

    const claimed = claimedRewardIds.includes(reward.id);
    const ready = !claimed && progress >= reward.target;

    return {
      ...reward,
      title:
        reward.category === "invite"
          ? t("rewards.journey.inviteFemaleFriends", {
              defaultValue: reward.title,
              count: reward.target,
            })
          : reward.category === "level"
          ? t("rewards.journey.reachLevel", {
              defaultValue: reward.title,
              level: reward.target,
            })
          : reward.category === "call"
          ? t("rewards.journey.completeCallMinutes", {
              defaultValue: reward.title,
              minutes: reward.target,
            })
          : reward.title,
      progress,
      claimed,
      ready,
      cta: claimed
        ? t("rewards.status.claimed", { defaultValue: "تم الاستلام" })
        : ready
        ? t("rewards.actions.claimNow", { defaultValue: "استلام الآن" })
        : t("rewards.status.inProgress", { defaultValue: "قيد التقدم" }),
    };
  };

  const inviteRewards = useMemo(
    () => INVITE_REWARDS.map(enrichReward),
    [inviteCount, claimedRewardIds, t]
  );

  const levelRewards = useMemo(
    () => LEVEL_REWARDS.map(enrichReward),
    [femaleLevel, claimedRewardIds, t]
  );

  const callRewards = useMemo(
    () => CALL_REWARDS.map(enrichReward),
    [totalCallMinutes, claimedRewardIds, t]
  );

  const claimableRewards = useMemo(
    () => [...inviteRewards, ...levelRewards, ...callRewards].filter((r) => r.ready),
    [inviteRewards, levelRewards, callRewards]
  );

  const claimableTokens = useMemo(
    () =>
      claimableRewards.reduce((sum, reward) => {
        return reward.rewardType === "tokens" ? sum + reward.rewardValue : sum;
      }, 0),
    [claimableRewards]
  );

  const list = useMemo(() => {
    if (activeTab === "invite") return inviteRewards;
    if (activeTab === "level") return levelRewards;
    return callRewards;
  }, [activeTab, inviteRewards, levelRewards, callRewards]);

  const upcomingRewards = useMemo(() => {
    return [...inviteRewards, ...levelRewards, ...callRewards]
      .filter((item) => !item.claimed && !item.ready)
      .sort((a, b) => {
        const aLeft = Math.max(0, a.target - a.progress);
        const bLeft = Math.max(0, b.target - b.progress);
        return aLeft - bLeft;
      })
      .slice(0, 3);
  }, [inviteRewards, levelRewards, callRewards]);

  const handleClaim = async (reward) => {
    if (!reward?.id) return;

    try {
      setFeedback("");
      setClaimingId(reward.id);

      const res = await claimFemaleReward({
        rewardId: reward.id,
        type: reward.category,
      });

      const data = res?.data || {};

      setFeedback(
        data?.message ||
          t("rewards.messages.claimSuccess", {
            defaultValue: "تم استلام المكافأة بنجاح.",
          })
      );
    } catch (error) {
      console.error("claimFemaleReward error:", error);

      const msg =
        error?.message ||
        t("rewards.messages.claimError", {
          defaultValue: "حدث خطأ أثناء استلام المكافأة.",
        });

      setFeedback(msg);
    } finally {
      setClaimingId("");
    }
  };

  const handleClaimAllReady = async () => {
    for (const reward of claimableRewards) {
      // eslint-disable-next-line no-await-in-loop
      await handleClaim(reward);
    }
  };

  const renderCardIcon = (type) => {
    if (type === "invite") {
      return (
        <div className="rank-reward-card-art invite">
          <FaUserFriends />
        </div>
      );
    }

    if (type === "level") {
      return (
        <div className="rank-reward-card-art gift">
          <FaCrown />
        </div>
      );
    }

    if (type === "call") {
      return (
        <div className="rank-reward-card-art recharge">
          <FaPhoneAlt />
        </div>
      );
    }

    if (type === "gift") {
      return (
        <div className="rank-reward-card-art gift">
          <FaGift />
        </div>
      );
    }

    return (
      <div className="rank-reward-card-art coin">
        <img
          src={tokenImg}
          alt={t("rewards.journey.tokenAlt", { defaultValue: "tokens" })}
        />
      </div>
    );
  };

  const getRewardValueText = (value) =>
    t("rewards.journey.rewardValueTokens", {
      defaultValue: "{{count}} tokens",
      count: value,
    });

  if (loading) {
    return (
      <div className="rank-rewards-page">
        <TopBar
          title={t("rewards.journey.femaleTitle", { defaultValue: "رحلتي في Livora" })}
          onBack={() => navigate(-1)}
        />
        <div className="rank-rewards-content">
          <div className="rank-loading-state">
            {t("common.loading", { defaultValue: "جاري التحميل..." })}
          </div>
        </div>
        <BottomNav active="profile" />
      </div>
    );
  }

  if (gender === "male") {
    return null;
  }

  return (
    <div className="rank-rewards-page">
      <TopBar
        title={t("rewards.journey.femaleTitle", { defaultValue: "رحلتي في Livora" })}
        onBack={() => navigate(-1)}
      />

      <div className="rank-rewards-content">
        {!!feedback && <div className="rank-feedback-box">{feedback}</div>}

        <section className="rank-alert-banner">
          <div className="rank-alert-left">
            <div className="rank-alert-coin-wrap">
              <img
                src={tokenImg}
                alt={t("rewards.journey.tokenAlt", { defaultValue: "tokens" })}
                className="rank-alert-coin"
              />
            </div>
          </div>

          <div className="rank-alert-texts">
            <div className="rank-alert-title">
              {claimableTokens > 0
                ? t("rewards.journey.claimableTokensReady", {
                    defaultValue: "You have {{tokens}} tokens ready to claim!",
                    tokens: claimableTokens,
                  })
                : t("rewards.empty.claimable", {
                    defaultValue: "لا توجد مكافآت جاهزة الآن",
                  })}
            </div>

            <div className="rank-alert-subtitle">
              {claimableTokens > 0
                ? t("rewards.journey.claimableSubtitleFemale", {
                    defaultValue: "استلمي مكافآتك الآن وواصلي التقدّم في رحلتك.",
                  })
                : t("rewards.journey.emptyClaimableSubtitleFemale", {
                    defaultValue:
                      "أكملي الدعوات أو المستويات أو دقائق الاتصال لفتح المكافآت القادمة.",
                  })}
            </div>
          </div>

          <button
            className="rank-alert-btn"
            onClick={handleClaimAllReady}
            disabled={!claimableRewards.length || !!claimingId}
          >
            {claimingId
              ? t("common.loading", { defaultValue: "جاري التنفيذ..." })
              : t("rewards.actions.claimNow", { defaultValue: "استلام الآن" })}
          </button>
        </section>

        <section className="rank-tabs-section">
          <div className="rank-tabs">
            <button
              className={`rank-tab ${activeTab === "invite" ? "active" : ""}`}
              onClick={() => setActiveTab("invite")}
            >
              {t("rewards.categories.invite", { defaultValue: "مكافآت الدعوات" })}
            </button>

            <button
              className={`rank-tab ${activeTab === "level" ? "active" : ""}`}
              onClick={() => setActiveTab("level")}
            >
              {t("rewards.categories.level", { defaultValue: "مكافآت المستوى" })}
            </button>

            <button
              className={`rank-tab ${activeTab === "call" ? "active" : ""}`}
              onClick={() => setActiveTab("call")}
            >
              {t("rewards.categories.call", { defaultValue: "مكافآت الاتصال" })}
            </button>
          </div>
        </section>

        <section className="rank-rewards-list">
          {list.map((item) => {
            const percent = Math.max(
              0,
              Math.min(100, Math.round((item.progress / item.target) * 100))
            );

            return (
              <div
                key={item.id}
                className={`rank-reward-card ${item.ready ? "ready" : ""} ${
                  item.claimed ? "claimed" : ""
                }`}
              >
                <div className="rank-reward-top">
                  <div className="rank-reward-main">
                    {renderCardIcon(item.icon)}

                    <div className="rank-reward-texts">
                      <div className="rank-reward-title">{item.title}</div>
                      <div className="rank-reward-subtitle">
                        {getRewardValueText(item.rewardValue)}
                      </div>
                    </div>
                  </div>

                  <button
                    className={`rank-reward-cta ${
                      item.ready ? "ready" : ""
                    } ${item.claimed ? "claimed" : ""}`}
                    disabled={!item.ready || !!claimingId}
                    onClick={() => handleClaim(item)}
                  >
                    {claimingId === item.id
                      ? t("common.loading", { defaultValue: "جاري التنفيذ..." })
                      : item.cta}
                  </button>
                </div>

                <div className="rank-reward-progress">
                  <div className="rank-reward-progress-bar">
                    <div
                      className="rank-reward-progress-fill"
                      style={{ width: `${percent}%` }}
                    />
                  </div>

                  <div className="rank-reward-progress-value">
                    {item.progress} / {item.target}
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section className="rank-upcoming-section">
          <div className="rank-section-head">
            <div className="rank-section-title">
              {t("rewards.upcoming.title", { defaultValue: "الجوائز القادمة" })}
            </div>

            <button className="rank-section-link" type="button">
              <span>{t("rewards.actions.viewAll", { defaultValue: "عرض الكل" })}</span>
              <FaChevronLeft />
            </button>
          </div>

          <div className="rank-upcoming-list">
            {upcomingRewards.map((item) => {
              const percent = Math.max(
                0,
                Math.min(100, Math.round((item.progress / item.target) * 100))
              );

              return (
                <div key={item.id} className="rank-upcoming-card">
                  <div className="rank-upcoming-icon">
                    <img
          src={tokenImg}
          alt={t("rewards.journey.tokenAlt", { defaultValue: "tokens" })}
        />
                  </div>

                  <div className="rank-upcoming-body">
                    <div className="rank-upcoming-title">{item.title}</div>
                    <div className="rank-upcoming-subtitle">
                      {getRewardValueText(item.rewardValue)}
                    </div>

                    <div className="rank-upcoming-progress-row">
                      <div className="rank-upcoming-progress-bar">
                        <div
                          className="rank-upcoming-progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>

                      <div className="rank-upcoming-progress-value">
                        {item.progress} / {item.target}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {!upcomingRewards.length && (
              <div className="rank-empty-upcoming">
                {t("rewards.empty.upcoming", {
                  defaultValue: "لا توجد مكافآت قادمة حاليًا.",
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      <BottomNav active="profile" />
    </div>
  );
}
